import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Briefcase, MapPin, Stethoscope, Building2, Shield, Scale, GraduationCap, ChevronDown, ChevronUp, Loader2, Globe, ChevronLeft, ArrowLeft, Home, Landmark, Map, Building } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { OposaiLogo } from '@/components/TuPlazaIALogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_INFO = {
  sanidad: { icon: Stethoscope, label: '🏥 Sanidad', color: 'emerald' },
  administracion: { icon: Building2, label: '🏛️ Administración', color: 'blue' },
  seguridad: { icon: Shield, label: '🚔 Seguridad', color: 'red' },
  justicia: { icon: Scale, label: '⚖️ Justicia', color: 'purple' },
  educacion: { icon: GraduationCap, label: '📚 Educación', color: 'amber' }
};

// Scope options: Estatal, Comunidad Autónoma, Local
const SCOPE_OPTIONS = [
  { id: 'estatal', label: 'Estatal', icon: Landmark, description: 'Ámbito nacional', emoji: '🏛️' },
  { id: 'autonomico', label: 'Comunidad Autónoma', icon: Map, description: 'Ámbito autonómico', emoji: '🗺️' },
  { id: 'local', label: 'Local', icon: Building, description: 'Ayuntamientos y municipios', emoji: '🏘️' }
];

const ProfessionSelector = ({ user }) => {
  const navigate = useNavigate();
  const [professions, setProfessions] = useState([]);
  const [regions, setRegions] = useState([]);
  const [selectedProfession, setSelectedProfession] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedScope, setSelectedScope] = useState(''); // New: scope selection
  const [expandedCategory, setExpandedCategory] = useState('sanidad');
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [country, setCountry] = useState(null);
  const [regionsLabel, setRegionsLabel] = useState('Comunidad Autónoma');
  
  // Check if user already has a profession selected (can go back to dashboard)
  const canGoToDashboard = user?.selected_profession;

  useEffect(() => {
    // Check if user has selected country
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const userCountry = currentUser.selected_country || 'ES';
    
    if (!userCountry) {
      navigate('/select-country');
      return;
    }
    
    loadData(userCountry);
  }, []);

  const loadData = async (countryCode) => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Load country details and professions
      const [countryRes, profsRes] = await Promise.all([
        axios.get(`${API}/countries/${countryCode}`, { headers }),
        axios.get(`${API}/countries/${countryCode}/professions`, { headers })
      ]);
      
      setCountry(countryRes.data);
      setRegions(countryRes.data.regions || []);
      setRegionsLabel(countryRes.data.regions_label || 'Región');
      
      if (profsRes.data.available && profsRes.data.professions) {
        setProfessions(profsRes.data.professions);
      } else {
        // Country not available yet
        toast.info(profsRes.data.message || 'Próximamente disponible');
        setProfessions([]);
      }
      
    } catch (error) {
      // Fallback to default Spain professions
      try {
        const token = localStorage.getItem('session_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const profRes = await axios.get(`${API}/professions`, { headers });
        setProfessions(profRes.data);
        
        // Default Spain regions
        setRegions([
          { id: 'andalucia', name: 'Andalucía' },
          { id: 'aragon', name: 'Aragón' },
          { id: 'asturias', name: 'Asturias' },
          { id: 'baleares', name: 'Islas Baleares' },
          { id: 'canarias', name: 'Canarias' },
          { id: 'cantabria', name: 'Cantabria' },
          { id: 'castilla_leon', name: 'Castilla y León' },
          { id: 'castilla_mancha', name: 'Castilla-La Mancha' },
          { id: 'cataluna', name: 'Cataluña' },
          { id: 'madrid', name: 'Madrid' },
          { id: 'murcia', name: 'Murcia' },
          { id: 'navarra', name: 'Navarra' },
          { id: 'pais_vasco', name: 'País Vasco' },
          { id: 'valencia', name: 'Comunidad Valenciana' },
          { id: 'galicia', name: 'Galicia' },
          { id: 'extremadura', name: 'Extremadura' },
          { id: 'la_rioja', name: 'La Rioja' },
          { id: 'ceuta', name: 'Ceuta' },
          { id: 'melilla', name: 'Melilla' }
        ]);
      } catch (e) {
        toast.error('Error al cargar datos');
      }
    } finally {
      setLoadingData(false);
    }
  };

  // Agrupar profesiones por categoría
  const professionsByCategory = professions.reduce((acc, prof) => {
    const cat = prof.category || 'otros';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(prof);
    return acc;
  }, {});

  const handleSelectProfession = (professionId) => {
    setSelectedProfession(professionId);
    // Reset scope and region when changing profession
    setSelectedScope('');
    setSelectedRegion('');
  };

  const handleSelectScope = (scopeId) => {
    setSelectedScope(scopeId);
    // Auto-set region for 'estatal' scope
    if (scopeId === 'estatal') {
      setSelectedRegion('estatal');
    } else {
      setSelectedRegion('');
    }
  };

  const handleContinue = async () => {
    if (!selectedProfession) {
      toast.error('Selecciona una profesión');
      return;
    }
    if (!selectedScope) {
      toast.error('Selecciona el ámbito de tu oposición');
      return;
    }
    if (selectedScope !== 'estatal' && !selectedRegion) {
      toast.error(`Selecciona tu ${selectedScope === 'local' ? 'localidad' : 'comunidad autónoma'}`);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      
      // Build region value based on scope
      const regionValue = selectedScope === 'estatal' ? 'estatal' : selectedRegion;
      
      await axios.post(
        `${API}/user/select-profession`,
        { 
          profession_id: selectedProfession, 
          region: regionValue,
          scope: selectedScope 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local user
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      currentUser.selected_profession = selectedProfession;
      currentUser.selected_region = regionValue;
      currentUser.selected_scope = selectedScope;
      localStorage.setItem('user', JSON.stringify(currentUser));
      
      toast.success('¡Configuración guardada! Bienvenido a TuPlazaIA');
      
      // Check if user needs to select plan
      try {
        const accessRes = await axios.get(`${API}/system/check-access`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (accessRes.data.require_plan_selection && !accessRes.data.subscription_tier) {
          navigate('/select-plan');
        } else {
          navigate('/dashboard');
        }
      } catch {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error saving profession:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setLoading(false);
    }
  };

  const selectedProf = professions.find(p => p.profession_id === selectedProfession);

  if (loadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      {/* Top Navigation */}
      <nav className="border-b border-slate-200 bg-white/70 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            {/* Back to Dashboard button */}
            {canGoToDashboard && (
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Volver al Dashboard</span>
                <Home className="w-4 h-4 sm:hidden" />
              </Button>
            )}
            <div className={canGoToDashboard ? "" : "mx-auto"}>
              <OposaiLogo size={45} />
            </div>
            {canGoToDashboard && <div className="w-20" />}
          </div>
        </div>
      </nav>
      
      <div className="max-w-4xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="text-center mb-8">
          {/* Country badge */}
          {country && (
            <button 
              onClick={() => navigate('/select-country')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-full text-sm text-slate-600 hover:bg-slate-50 mb-4"
            >
              <span className="text-lg">{country.flag}</span>
              <span>{country.name}</span>
              <ChevronDown className="w-4 h-4" />
            </button>
          )}
          
          <h1 className="text-3xl md:text-4xl font-bold font-heading mb-3">¡Bienvenido, {user?.name?.split(' ')[0] || 'Opositor'}!</h1>
          <p className="text-lg text-slate-600">
            Selecciona tu oposición para personalizar tu experiencia de estudio
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Columna de selección de profesión */}
          <div className="md:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
              <div className="p-4 bg-slate-50 border-b border-slate-200">
                <h2 className="font-bold text-lg flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-indigo-600" />
                  Selecciona tu oposición
                </h2>
              </div>
              
              <div className="max-h-[500px] overflow-y-auto">
                {Object.entries(CATEGORY_INFO).map(([catKey, catInfo]) => {
                  const Icon = catInfo.icon;
                  const profsInCat = professionsByCategory[catKey] || [];
                  const isExpanded = expandedCategory === catKey;
                  
                  if (profsInCat.length === 0) return null;
                  
                  return (
                    <div key={catKey} className="border-b border-slate-100 last:border-b-0">
                      <button
                        onClick={() => setExpandedCategory(isExpanded ? '' : catKey)}
                        className={`w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors ${
                          isExpanded ? 'bg-slate-50' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl bg-${catInfo.color}-100 flex items-center justify-center`}>
                            <Icon className={`w-5 h-5 text-${catInfo.color}-600`} />
                          </div>
                          <div className="text-left">
                            <p className="font-semibold text-slate-900">{catInfo.label}</p>
                            <p className="text-xs text-slate-500">{profsInCat.length} oposiciones</p>
                          </div>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </button>
                      
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="px-4 pb-3 space-y-1">
                              {profsInCat.map((prof) => {
                                // Profesión disponible si tiene is_active=true o has_content=true
                                // O si es la profesión específica de Enfermería de Urgencias
                                const isAvailable = prof.is_active === true || 
                                                   prof.has_content === true ||
                                                   prof.profession_id === 'prof_enf_urgencias_ap' || 
                                                   (prof.name.toLowerCase().includes('urgencias') && 
                                                    prof.name.toLowerCase().includes('atención primaria'));
                                
                                return (
                                  <button
                                    key={prof.profession_id}
                                    onClick={() => isAvailable ? handleSelectProfession(prof.profession_id) : toast.info('¡Próximamente disponible!')}
                                    className={`w-full text-left px-4 py-3 rounded-xl transition-all ${
                                      !isAvailable 
                                        ? 'bg-slate-100 cursor-not-allowed opacity-60' 
                                        : selectedProfession === prof.profession_id
                                          ? 'bg-indigo-100 border-2 border-indigo-500'
                                          : 'bg-slate-50 hover:bg-slate-100 border-2 border-transparent'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <div className="flex items-center gap-2">
                                          <p className="font-medium text-slate-900">{prof.name}</p>
                                          {!isAvailable && (
                                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                                              Próximamente
                                            </span>
                                          )}
                                          {isAvailable && (
                                            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                                              ¡Disponible!
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      {selectedProfession === prof.profession_id && isAvailable && (
                                        <div className="w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center">
                                          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                        </div>
                                      )}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Columna de ámbito, región y resumen */}
          <div className="space-y-4">
            {/* Selector de Ámbito - Estatal/Autonómico/Local */}
            {selectedProfession && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl shadow-lg border border-slate-200 p-4"
              >
                <h3 className="font-bold text-sm mb-3 flex items-center gap-2 text-slate-700">
                  <Landmark className="w-4 h-4 text-indigo-600" />
                  Ámbito de la oposición
                </h3>
                
                <div className="space-y-2">
                  {SCOPE_OPTIONS.map((scope) => {
                    const ScopeIcon = scope.icon;
                    return (
                      <button
                        key={scope.id}
                        onClick={() => handleSelectScope(scope.id)}
                        className={`w-full text-left px-4 py-3 rounded-xl transition-all flex items-center gap-3 ${
                          selectedScope === scope.id
                            ? 'bg-indigo-100 border-2 border-indigo-500'
                            : 'bg-slate-50 hover:bg-slate-100 border-2 border-transparent'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          selectedScope === scope.id ? 'bg-indigo-500' : 'bg-slate-200'
                        }`}>
                          <ScopeIcon className={`w-5 h-5 ${selectedScope === scope.id ? 'text-white' : 'text-slate-600'}`} />
                        </div>
                        <div className="flex-1">
                          <p className={`font-medium ${selectedScope === scope.id ? 'text-indigo-700' : 'text-slate-800'}`}>
                            {scope.emoji} {scope.label}
                          </p>
                          <p className="text-xs text-slate-500">{scope.description}</p>
                        </div>
                        {selectedScope === scope.id && (
                          <div className="w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}
            
            {/* Selector de región - Solo si ámbito es autonómico o local */}
            {selectedScope && selectedScope !== 'estatal' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl shadow-lg border border-slate-200 p-4"
              >
                <h3 className="font-bold text-sm mb-3 flex items-center gap-2 text-slate-700">
                  <MapPin className="w-4 h-4 text-indigo-600" />
                  {selectedScope === 'local' ? 'Selecciona tu Comunidad Autónoma' : regionsLabel}
                </h3>
                
                <div className="max-h-60 overflow-y-auto space-y-1">
                  {regions.map((region) => {
                    const regionId = region.id || region.name.toLowerCase().replace(/ /g, '_');
                    return (
                      <button
                        key={regionId}
                        onClick={() => setSelectedRegion(regionId)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                          selectedRegion === regionId
                            ? 'bg-indigo-100 text-indigo-700 font-medium'
                            : 'hover:bg-slate-100 text-slate-700'
                        }`}
                      >
                        {region.name}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Resumen de selección */}
            {selectedProf && selectedScope && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl p-4 text-white"
              >
                <h3 className="font-bold text-sm mb-3 opacity-80">Tu selección</h3>
                <div className="space-y-2">
                  <p className="font-bold">{selectedProf.name}</p>
                  <p className="text-sm opacity-80">
                    {SCOPE_OPTIONS.find(s => s.id === selectedScope)?.emoji} {SCOPE_OPTIONS.find(s => s.id === selectedScope)?.label}
                  </p>
                  {selectedScope !== 'estatal' && selectedRegion && (
                    <p className="text-sm opacity-80">
                      📍 {regions.find(r => (r.id || r.name.toLowerCase().replace(/ /g, '_')) === selectedRegion)?.name}
                    </p>
                  )}
                </div>
              </motion.div>
            )}

            {/* Botón continuar */}
            <Button
              onClick={handleContinue}
              disabled={loading || !selectedProfession || !selectedScope || (selectedScope !== 'estatal' && !selectedRegion)}
              className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700 text-white py-6 text-lg font-bold shadow-lg disabled:opacity-50"
              data-testid="continue-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                'Comenzar a Estudiar'
              )}
            </Button>

            {/* Info trial */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
              <p className="text-sm text-emerald-700">
                <strong>7 días de prueba gratuita</strong>
                <br />
                <span className="text-xs">Sin tarjeta de crédito</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfessionSelector;
